import { X, Search, Sparkles, Star, Filter, Package } from 'lucide-react';
import { useState, useMemo } from 'react';

import ArrowTooltip from '@/components/custom-ui/customTooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

type Props = {
  items: { label: string; value: number; isImportant: boolean }[];
  selectedItems: number[];
  setSelectedItems: React.Dispatch<React.SetStateAction<number[]>>;
};

const ItemSelection = ({ items, selectedItems, setSelectedItems }: Props) => {
  const [searchText, setSearchText] = useState('');
  const [showImportantOnly, setShowImportantOnly] = useState(false);

  const toggleSelection = (id: number) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const filteredItems = useMemo(() => {
    return items.filter(({ label, isImportant }) => {
      const search = searchText.trim().toLowerCase();
      const matchesSearch = label.toLowerCase().includes(search);
      const matchesImportant = !showImportantOnly || isImportant;
      return matchesSearch && matchesImportant;
    });
  }, [items, searchText, showImportantOnly]);

  const importantItems = useMemo(() => 
    items.filter((item) => item.isImportant),
    [items]
  );

  const selectImportantItems = () => {
    setSelectedItems(importantItems.map((item) => item.value));
  };

  const clearSelection = () => {
    setSelectedItems([]);
    setSearchText('');
    setShowImportantOnly(false);
  };

  return (
    <Card className="w-full border-0 bg-gradient-to-br from-muted/30 to-muted/10 p-3">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-primary/10 p-1.5">
              <Package className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-semibold">أصناف الشركة</h3>
              <p className="text-xs text-muted-foreground">
                {items.length} صنف متاح
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {selectedItems.length > 0 && (
              <>
                <Badge 
                  variant="outline" 
                  className="bg-primary/5 border-primary/20 text-primary text-xs"
                >
                  {selectedItems.length} محدد
                </Badge>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 hover:bg-destructive/10 hover:text-destructive"
                  onClick={clearSelection}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="بحث في الأصناف..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pr-10 h-9 text-sm bg-background/50 border-border/50 focus:bg-background transition-colors"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button
              variant={showImportantOnly ? "default" : "outline"}
              size="sm"
              onClick={() => setShowImportantOnly(!showImportantOnly)}
              className={cn(
                "h-8 text-xs transition-all",
                showImportantOnly && "bg-amber-500 hover:bg-amber-600 dark:bg-amber-600 dark:hover:bg-amber-700"
              )}
            >
              <Star className={cn(
                "h-3.5 w-3.5 ml-1",
                showImportantOnly && "fill-current"
              )} />
              المهمة فقط
            </Button>
            
            {importantItems.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={selectImportantItems}
                className="h-8 text-xs hover:bg-primary/10 hover:text-primary hover:border-primary/30"
              >
                <Sparkles className="h-3.5 w-3.5 ml-1" />
                تحديد ({importantItems.length})
              </Button>
            )}
          </div>
        </div>

        <ScrollArea className="h-56 rounded-lg border border-border/50 bg-background/30">
          <div className="p-1">
            {filteredItems.length > 0 ? (
              <div className="space-y-1">
                {filteredItems.map(({ label, value, isImportant }) => (
                  <div
                    key={value}
                    className={cn(
                      "flex items-center gap-2 rounded-lg px-2.5 py-2 transition-all",
                      "hover:bg-muted/50 dark:hover:bg-muted/30",
                      selectedItems.includes(value) && "bg-primary/5 hover:bg-primary/10"
                    )}
                  >
                    <Checkbox
                      checked={selectedItems.includes(value)}
                      onCheckedChange={() => toggleSelection(value)}
                      className="data-[state=checked]:bg-primary data-[state=checked]:border-primary h-4 w-4"
                    />
                    
                    <span className={cn(
                      "flex-1 text-xs truncate",
                      selectedItems.includes(value) && "font-medium"
                    )}>
                      {label}
                    </span>
                    
                    {isImportant && (
                      <ArrowTooltip color="primary" msg="صنف مهم">
                        <Badge 
                          variant="outline"
                          className="h-4 px-1 bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400"
                        >
                          <Star className="h-2.5 w-2.5 fill-current" />
                        </Badge>
                      </ArrowTooltip>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Filter className="h-8 w-8 text-muted-foreground/30 mb-2" />
                <p className="text-xs text-muted-foreground">
                  لا توجد نتائج مطابقة
                </p>
                {searchText && (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    جرب البحث بكلمات أخرى
                  </p>
                )}
              </div>
            )}
          </div>
        </ScrollArea>

        {filteredItems.length > 0 && (
          <div className="flex items-center justify-between px-2 py-1 bg-muted/20 rounded-lg">
            <span className="text-[10px] text-muted-foreground">
              {filteredItems.length} من {items.length} صنف
            </span>
            {showImportantOnly && (
              <Badge variant="outline" className="h-4 text-[10px] bg-amber-500/10 border-amber-500/30">
                عرض المهمة فقط
              </Badge>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default ItemSelection;